/**
 * GP-482 — the one keydown listener behind every `Shift + <key>` shortcut.
 *
 * The shortcuts are deliberately bare (`Shift + B`, not `Ctrl + Shift + B`), so
 * the guards below are what make them safe. A shortcut fires only when:
 *   - Shift is the ONLY modifier (Ctrl/Alt/Cmd combos belong to the browser),
 *   - focus is not in something that eats typing (input, textarea, MUI
 *     Select/Autocomplete, a DataGrid edit cell, any contenteditable),
 *   - no modal dialog is open — Shift + B must not yank a half-finished
 *     Process Billing wizard out from under the user,
 *   - no other overlay (temporary Drawer, Popover/Menu, open listbox) is on
 *     screen — same reasoning as the dialog guard,
 *   - and no OTHER keydown lands within SHORTCUT_SCANNER_GUARD_MS: HID
 *     barcode scanners type Shift+letter for every uppercase character at
 *     ~10 ms per key, so a scan containing "B" with the scan field unfocused
 *     is indistinguishable from the Shift+B chord key-by-key. A human
 *     shortcut is a lone keystroke; a scan is a burst. The action is armed on
 *     keydown and fires only if the guard window passes with no follow-up key.
 *
 * The key is read from `event.code` for digits, so `Shift + 1` stays "1" on
 * layouts where it produces `!`, `+`, `"` or anything else.
 *
 * Registry of what's bound: `constants/keyboardShortcuts.ts`.
 */

import { useEffect, useRef } from 'react';

/** Handlers keyed by the normalized key, e.g. `{ b: goToBilling, '1': firstTab }`. */
export type ShortcutHandlers = Record<string, (() => void) | undefined>;

export interface UseShiftShortcutsOptions {
  /** Unbind without unmounting — e.g. a queue that is not the active tab. */
  enabled?: boolean;
  /**
   * Keep firing while a MUI Dialog is open. Off by default; only screens that
   * live INSIDE a dialog (the Process Billing wizard) should turn it on.
   */
  allowWhileDialogOpen?: boolean;
}

/**
 * True when the keystroke belongs to whatever the user is typing into.
 * Covers the plain form elements plus the MUI widgets that render a div with
 * a textbox/combobox role instead of a real `<input>`.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element || typeof element.closest !== 'function') return false;
  if (element.isContentEditable) return true;

  const tag = element.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;

  return Boolean(
    element.closest(
      '[contenteditable="true"], [role="textbox"], [role="searchbox"], [role="combobox"], .MuiDataGrid-editInputCell',
    ),
  );
}

/**
 * True when a MUI Dialog is on screen. Dialogs are unmounted when closed by
 * default, but `keepMounted` ones stay in the DOM hidden — so presence alone
 * isn't enough, the computed style has to say it's actually visible.
 */
export function isDialogOpen(): boolean {
  if (typeof document === 'undefined') return false;
  return isVisibleInDom('.MuiDialog-root');
}

/**
 * True when a non-Dialog overlay is on screen: a temporary Drawer, a
 * Popover/Menu, or an open listbox/menu (Autocomplete popper, Select menu).
 * Kept separate from isDialogOpen because `allowWhileDialogOpen` opts a screen
 * back in for Dialogs only — a menu floating over that screen must still
 * swallow the shortcut. `.MuiDrawer-modal` (not `.MuiDrawer-root`) so the
 * app's permanent sidebar Drawer doesn't disable shortcuts everywhere.
 */
export function isBlockingOverlayOpen(): boolean {
  if (typeof document === 'undefined') return false;
  return isVisibleInDom(
    '.MuiDrawer-modal, .MuiPopover-root, .MuiMenu-root, [role="listbox"], [role="menu"]',
  );
}

/** Any element matching `selector` that is actually visible (keepMounted-aware). */
function isVisibleInDom(selector: string): boolean {
  return Array.from(document.querySelectorAll(selector)).some((element) => {
    const style = window.getComputedStyle(element);
    return style.visibility !== 'hidden' && style.display !== 'none';
  });
}

/**
 * How long a matched shortcut stays provisional before it fires. Any other
 * keydown inside this window cancels it: a barcode scanner emits keys at
 * roughly 10 ms intervals, so the next character of a scan always lands well
 * inside the window, while a human pressing a deliberate Shift+letter chord
 * does not follow it with another key within 75 ms.
 */
export const SHORTCUT_SCANNER_GUARD_MS = 75;

/** Bare modifier presses never cancel a pending shortcut — holding Shift IS the chord. */
const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta']);

/**
 * Normalize a keydown to the key our registry stores.
 * Digits come from `event.code` (layout-independent: `Shift + 1` is `!` on a US
 * keyboard); everything else from the lower-cased `event.key`, so `Shift + /`
 * arrives as the `?` the help sheet is registered under.
 */
export function normalizeShortcutKey(event: Pick<KeyboardEvent, 'key' | 'code'>): string {
  const digit = /^Digit([0-9])$/.exec(event.code || '');
  if (digit) return digit[1];
  return event.key.length === 1 ? event.key.toLowerCase() : event.key;
}

export function useShiftShortcuts(
  handlers: ShortcutHandlers,
  { enabled = true, allowWhileDialogOpen = false }: UseShiftShortcutsOptions = {},
): void {
  // Re-reading handlers from a ref keeps the listener stable across renders —
  // queue pages rebuild these callbacks on every data refresh.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // The one provisional shortcut, if any — armed on keydown, fired after
  // SHORTCUT_SCANNER_GUARD_MS, cancelled by any intervening non-modifier key.
  const pendingTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const cancelPending = () => {
      if (pendingTimerRef.current !== null) {
        window.clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      // Scanner-burst guard: ANY further keydown (except a bare modifier)
      // inside the window means these keystrokes are a stream — a barcode
      // being typed at ~10 ms/char — not a deliberate chord. Cancel first so
      // a scan like "…B4…" never navigates mid-scan.
      if (!MODIFIER_KEYS.has(event.key)) cancelPending();

      if (!event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;
      if (event.repeat) return;
      if (isTypingTarget(event.target)) return;
      if (!allowWhileDialogOpen && isDialogOpen()) return;
      if (isBlockingOverlayOpen()) return;

      const handler = handlersRef.current[normalizeShortcutKey(event)];
      if (!handler) return;

      event.preventDefault();
      pendingTimerRef.current = window.setTimeout(() => {
        pendingTimerRef.current = null;
        handler();
      }, SHORTCUT_SCANNER_GUARD_MS);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      cancelPending();
    };
  }, [enabled, allowWhileDialogOpen]);
}

export default useShiftShortcuts;
